
import {
  ButtonBackToDashboard,
  FieldPTable,
  FieldPTableType,
  PButton,
  PFormSelect,
  PPersonalizeTable,
  PSidebar,
  PTab,
  PTable,
  PTableType,
  PTabs,
  RawFieldPTable,
} from "@/common/components";
import PIcon from "@/common/components/look/PIcon/PIcon.vue";
import {
  EEarlyValueNotificationType,
  ETransactionOrigin,
  ETransactionTypeList,
  LogicCashToday,
  ServicePoint,
  Transaction,
  TransactionDefaultFields,
  TransactionSearchConfig,
  UserPreferences,
} from "@/entities";
import { RawGridField, RawReportField, UserPreferencesReport } from "@/entities/user-preferences-report";
import { i18n } from "@/i18n";
import DateTimeMixin from "@/mixins/datetime";
import { OrderByFields } from "@/services";
import { FilterOperator } from "@/services/backend/operators";
import VueMethods from "@/vue-methods";
import { Instant, LocalDateTime } from "@js-joda/core";
import { Component, Vue, Watch } from "vue-property-decorator";
import { Getter } from "vuex-class";
import DetailTransaction from "./DetailTransaction.vue";
import FilterTransactionsPage from "./FilterTransactionsPage.vue";
import { FilterTransations, TransactionItem } from "./transactionsPage.type";

@Component({
  name: "transactions-page",
  mixins: [DateTimeMixin, VueMethods],
  components: {
    PButton,
    PTable,
    PTabs,
    PTab,
    PSidebar,
    PFormSelect,
    FilterTransactionsPage,
    PPersonalizeTable,
    DetailTransaction,
    ButtonBackToDashboard,
    PIcon,
  },
})
/* eslint-disable @typescript-eslint/no-explicit-any */
export default class TransactionsPage extends Vue {
  /**
   * Returns the browser language in lowercase (e.g. 'en', 'es')
   */
  getBrowserLanguage(): string {
    const navLang = navigator.language ?? "en";
    return navLang.split(/[-_]/)[0].toLowerCase();
  }
  @Getter("getTransactionFields") getTransactionFields!: FieldPTable[];
  @Getter("getTransactionFieldGroups") getTransactionFieldGroups!: string[];
  @Getter("getTransactionsSearchConfig") getTransactionsSearchConfig!: TransactionSearchConfig;
  @Getter("getTransactionsDefaultFields") getTransactionsDefaultFields!: TransactionDefaultFields;
  language = i18n.locale;
  canRequestExport = true;
  servicePointId = "";
  servicePoints: ServicePoint[] | undefined = [];
  @Watch("servicePoints", { deep: true, immediate: true })
  onChangeServicePoints(): void {
    this.updateTransactionsFieldsPTable();
    this.updateTransactionsFieldsReportPTable();
  }
  logicCashTodayId = "";
  logicCashTodays: LogicCashToday[] | undefined = [];
  @Watch("logicCashTodays", { deep: true, immediate: true })
  onChangeLogicCashTodays(): void {
    this.updateTransactionsFieldsPTable();
    this.updateTransactionsFieldsReportPTable();
  }
  transactions: Transaction[] = [];
  transactionItems: TransactionItem[] = [];
  iTranslationsTable: any = { empty: this.$t("transactions.haventSearch") };
  showFilter = true;
  showSideBar = false;
  title = this.$t("transactions.detailsTitle");
  showPersonalize = false;
  showDetail = false;
  widthSidebar = "";
  pageSize = 20;
  selectedTransaction: TransactionItem | Transaction | null = null;
  eTransactionTypes = ETransactionTypeList;
  filtersServerParams: any[] = [];
  filters: FilterTransations = {
    dateStart: new Date(),
    dateEnd: new Date(),
    transactionType: [
      this.eTransactionTypes.SHIPOUT,
      this.eTransactionTypes.V_SHIPOUT,
      this.eTransactionTypes.CASHIN,
      this.eTransactionTypes.CASHOUT,
      this.eTransactionTypes.SHIPIN,
    ],
    entityName: "",
    userTransaction: "",
    creditDateStart: null,
    creditDateEnd: null,
    isCheckAccreditation: false,
    entityNameAccrediting: "",
    smartDevices: [],
  };
  @Watch("filters", { deep: true, immediate: true })
  onChangeFilters(): void {
    this.updateTransactionsFieldsPTable();
    this.updateTransactionsFieldsReportPTable();
  }
  paginationDefault = {
    pageSize: this.pageSize,
    pageSizeDefault: this.pageSize,
    totalElements: 0,
    currentPage: 1,
    limit: 300,
  };
  orderDefault = {
    orderFields: [{ field: "transactionDate", direction: "ASC" }],
    groupedFields: [],
  };
  transactionsCustomerFields: FieldPTable[] = [];
  transactionsCustomerReportFields: FieldPTable[] = [];
  extraDataFields: FieldPTable[] = [];
  paramsFilterTransactions: PTableType = {
    fields: [],
    defaultFields: [],
    fieldsReport: [],
    defaultFieldsReport: [],
    pagination: this.paginationDefault,
    order: this.orderDefault,
    groupFields: [],
  };

  private lastSavedPageSize?: number;

  private getTransactionsPagePrefs(): any {
    if (!this.userPreferencesReport) this.userPreferencesReport = {} as any;
    if (!this.userPreferencesReport.transactionsPage) this.userPreferencesReport.transactionsPage = {} as any;

    const tp = this.userPreferencesReport.transactionsPage as any;
    tp.gridFields = Array.isArray(tp.gridFields) ? tp.gridFields : [];
    tp.reportFields = Array.isArray(tp.reportFields) ? tp.reportFields : [];
    tp.pagination = tp.pagination || { pageSize: this.pageSize };

    return tp;
  }

  private isNewUserTransactionsPrefs(tp: any): boolean {
    const gridEmpty = !Array.isArray(tp.gridFields) || tp.gridFields.length === 0;
    const reportEmpty = !Array.isArray(tp.reportFields) || tp.reportFields.length === 0;
    return gridEmpty && reportEmpty;
  }

  private isHiddenField(f: any): boolean {
    return f?.type === FieldPTableType.HIDDEN;
  }

  private isActionField(f: any): boolean {
    return f?.type === FieldPTableType.ACTION || f?.key === "actions";
  }

  private getVisibleGridFieldsFromFront(): any[] {
    return (this.paramsFilterTransactions.fields as any[]).filter(
      (f) => f && f.show === true && !this.isHiddenField(f) && !this.isActionField(f)
    );
  }

  private getVisibleReportFieldsFromFront(): any[] {
    return (this.paramsFilterTransactions.fieldsReport as any[]).filter(
      (f) => f && f.show === true && !this.isHiddenField(f)
    );
  }

  private buildGridFieldsPayloadFromFront(): any[] {
    const visibleGrid = this.getVisibleGridFieldsFromFront();
    return visibleGrid.map((f, idx) => ({
      key: f.key,
      orderId: idx + 1,
      showGridFilter: !!f.showFilterField,
      showGridOrder: !!f.showOrderField,
      width: f.width,
    }));
  }

  private buildReportFieldsPayloadFromFront(): any[] {
    const visibleReport = this.getVisibleReportFieldsFromFront();
    return visibleReport.map((f, idx) => ({
      key: f.key,
      orderId: idx + 1,
    }));
  }

  private buildPayloadPaginationOnly(pageSize: number): any {
    const tp = this.getTransactionsPagePrefs();
    return {
      ...this.userPreferencesReport,
      version: "2",
      transactionsPage: {
        ...tp,
        pagination: { pageSize },
      },
    };
  }

  private buildPayloadForNewUser(pageSize: number): any {
    const tp = this.getTransactionsPagePrefs();
    const gridFieldsPayload = this.buildGridFieldsPayloadFromFront();
    const reportFieldsPayload = this.buildReportFieldsPayloadFromFront();

    return {
      ...this.userPreferencesReport,
      version: "2",
      transactionsPage: {
        ...tp,
        gridFields: gridFieldsPayload,
        reportFields: reportFieldsPayload,
        pagination: { pageSize },
      },
    };
  }

  private shouldPersistPageSize(pageSize: number): boolean {
    if (!pageSize) return false;
    if (this.lastSavedPageSize === pageSize) return false;
    this.lastSavedPageSize = pageSize;
    return true;
  }

  @Watch("paramsFilterTransactions.pagination", { deep: true })
  onChangeParamsFilterTransactions(): void {
    if (!this.prefsHydrated) return;
    if (!this.userPreferencesReport?.transactionsPage) return;

    const pageSize = this.paramsFilterTransactions.pagination.pageSize;

    this.paginationDefault.pageSize = pageSize;
    this.paginationDefault.pageSizeDefault = pageSize;
    if (!this.shouldPersistPageSize(pageSize)) return;

    const tp = this.getTransactionsPagePrefs();
    const isNew = this.isNewUserTransactionsPrefs(tp);

    const payload = isNew ? this.buildPayloadForNewUser(pageSize) : this.buildPayloadPaginationOnly(pageSize);

    this.userPreferencesReport = payload;
    this.$services.userPreferences.saveUserPreferencesV2(payload);
  }

  userPreferences: UserPreferences = {} as UserPreferences;
  userPreferencesReport: UserPreferencesReport = {} as UserPreferencesReport;
  private userPrefsGridApplied = false;
  private userPrefsReportApplied = false;
  private prefsHydrated = false;

  ensureUserPreferencesReportVersion(): void {
    if (!this.userPreferencesReport) this.userPreferencesReport = {} as UserPreferencesReport;

    if (!this.userPreferencesReport.version) this.userPreferencesReport.version = "2";

    if (!this.userPreferencesReport.transactionsPage) {
      (this.userPreferencesReport as any).transactionsPage = {
        gridFields: [],
        reportFields: [],
        pagination: { pageSize: this.pageSize },
      };
    } else {
      this.userPreferencesReport.transactionsPage.gridFields =
        this.userPreferencesReport.transactionsPage.gridFields || [];
      this.userPreferencesReport.transactionsPage.reportFields =
        this.userPreferencesReport.transactionsPage.reportFields || [];
      this.userPreferencesReport.transactionsPage.pagination = this.userPreferencesReport.transactionsPage
        .pagination || { pageSize: this.pageSize };
    }
  }

  selectTypeExport = "";
  optionsTypeExport = [
    {
      text: this.$t("transactions.exports.csv"),
      value: "CSV",
    },
    {
      text: this.$t("transactions.exports.csv2"),
      value: "CSV2",
    },
    {
      text: this.$t("transactions.exports.xls"),
      value: "XLS",
    },
  ];
  typeExportTranslation = this.$t("transactions.export");
  isExportDisabledBySearchLimit = false;

  selectedNumberFormat: "US" | "EU" | null = "US";

  showNumberFormatModal = false;

  async created(): Promise<void> {
    this.logicCashTodayId = this.$route.params.logicCashTodayId;
    this.servicePointId = this.$route.params.servicePointId;

    Promise.all([
      this.$services.servicePoints.fetchServicePoints(),
      this.$services.userPreferences.fetchUserPreferencesV2(),
    ]).then((resp) => {
      this.servicePoints = resp[0].servicePoints;
      this.userPreferencesReport = resp[1];

      this.ensureUserPreferencesReportVersion();

      this.paramsFilterTransactions.defaultFields = this.getColumnsDefault();
      this.paramsFilterTransactions.defaultFieldsReport = this.getColumnsReportDefault();
      this.paramsFilterTransactions.groupFields = this.getTransactionFieldGroups;

      const savedPageSize = this.userPreferencesReport.transactionsPage.pagination?.pageSize;
      if (savedPageSize) {
        this.paginationDefault.pageSize = savedPageSize;
        this.paramsFilterTransactions.pagination.pageSize = savedPageSize;
      }

      this.updateTransactionsFieldsPTable();
      this.updateTransactionsFieldsReportPTable();
      this.fetchCanExport();
      this.prefsHydrated = true;
    });
  }

  async fetchCanExport() {
    const canExport = await this.$services.exportRequest.canRequestExport();
    this.canRequestExport = canExport;
  }

  get optionsNumberFormat() {
    return [
      {
        value: "US",
        title: this.$t("transactions.numberFormat.usTitle"), // e.g. "Formato US – 1,234.56"
        subtitle: this.$t("transactions.numberFormat.usSubtitle"), // e.g. 'Miles: coma "," | Decimales: punto "."'
      },
      {
        value: "EU",
        title: this.$t("transactions.numberFormat.euTitle"), // e.g. "Formato EU – 1.234,56"
        subtitle: this.$t("transactions.numberFormat.euSubtitle"), // e.g. 'Miles: punto "." | Decimales: coma ","'
      },
    ];
  }

  get numberFormatPlaceholder(): string {
    const base = this.$t("transactions.numberFormatTitle"); // e.g. "Formato importes"

    // If no explicit selection, show only the title
    if (!this.selectedNumberFormat) {
      return String(base);
    }

    // If user selected something, show short suffix
    const suffixKey =
      this.selectedNumberFormat === "EU" ? "transactions.numberFormat.euShort" : "transactions.numberFormat.usShort";

    return `${base} (${this.$t(suffixKey)})`;
  }

  toggleNumberFormatModal(): void {
    this.showNumberFormatModal = !this.showNumberFormatModal;
  }

  selectNumberFormat(value: string): void {
    this.selectedNumberFormat = value as "US" | "EU";
    this.showNumberFormatModal = false;
  }

  clearNumberFormat(): void {
    // Remove explicit choice, UI shows only the title again
    this.selectedNumberFormat = null;
    this.showNumberFormatModal = false;
  }

  mounted(): void {
    document.addEventListener("click", this.onGlobalClick);
  }

  beforeDestroy(): void {
    document.removeEventListener("click", this.onGlobalClick);
  }

  onGlobalClick(event: MouseEvent): void {
    const wrapper = this.$refs.numberFormatWrapper as HTMLElement | undefined;
    if (wrapper && !wrapper.contains(event.target as Node)) {
      this.showNumberFormatModal = false;
    }
  }
  get currentDecimalFormat(): "US" | "EU" {
    return this.selectedNumberFormat === "EU" ? "EU" : "US";
  }

  getColumnsDefault(): FieldPTable[] {
    const fields: FieldPTable[] = [];
    this.getTransactionsDefaultFields.transactionPage.fields
      .filter((value) => value.showInGrid)
      .forEach((defaultField) => {
        const field = this.getTransactionFields.find((value) => value.key === defaultField.key);
        if (field) {
          fields.push(FieldPTable.createFromDefaultField(field, defaultField));
        } else {
          fields.push(
            new FieldPTable({
              key: defaultField.key,
              label: defaultField.key,
              groupField: "" + i18n.t("transactions.field.groupFieldTransationData"),
              show: defaultField.showByDefault,
              showFilterField: defaultField.showGridFilter,
            })
          );
        }
      });
    return fields;
  }

  private sortFieldsByOrderId<T extends { orderId?: number }>(arr: T[]): T[] {
    return [...arr].sort((a, b) => {
      const ao = a.orderId ?? Number.MAX_SAFE_INTEGER;
      const bo = b.orderId ?? Number.MAX_SAFE_INTEGER;
      return ao - bo;
    });
  }

  getColumnsReportDefault(): FieldPTable[] {
    const fields: FieldPTable[] = [];

    this.getTransactionsDefaultFields.transactionPage.fields
      .filter((value) => value.showInReport)
      .forEach((defaultField) => {
        const field = this.getTransactionFields.find((value) => value.key === defaultField.key);
        if (field) {
          fields.push(FieldPTable.createFromDefaultFieldReport(field, defaultField));
        } else {
          fields.push(
            new FieldPTable({
              key: defaultField.key,
              label: defaultField.key,
              groupField: "" + i18n.t("transactions.field.groupFieldTransationReport"),
              show: defaultField.showByDefault,
            })
          );
        }
      });
    return fields;
  }
  setUserPreferences(): void {
    if (!this.userPreferencesReport?.transactionsPage) return;

    const rawGrid = Array.isArray(this.userPreferencesReport.transactionsPage.gridFields)
      ? this.userPreferencesReport.transactionsPage.gridFields
      : [];

    if (rawGrid.length > 0) {
      const normalizedGrid = rawGrid.map((rf: any, idx: number) => ({
        ...rf,
        orderId: rf.orderId ?? idx + 1,
        show: rf.show ?? true,
      }));

      this.transactionsCustomerFields = normalizedGrid.map((rawField: any) => {
        const field = this.paramsFilterTransactions.defaultFields.find((fi: any) => fi.key == rawField.key);
        return field ? FieldPTable.createAndMergeV2(field, rawField) : new FieldPTable(rawField);
      });
    }

    const rawReport = Array.isArray(this.userPreferencesReport.transactionsPage.reportFields)
      ? this.userPreferencesReport.transactionsPage.reportFields
      : [];

    if (rawReport.length > 0) {
      const normalizedReport = rawReport.map((rf: any, idx: number) => ({
        ...rf,
        orderId: rf.orderId ?? idx + 1,
        show: rf.show ?? true,
      }));

      this.transactionsCustomerReportFields = normalizedReport.map((rawField: any) => {
        const field = this.paramsFilterTransactions.defaultFieldsReport.find((fi: any) => fi.key == rawField.key);
        return field ? FieldPTable.createAndMergeReportV2(field, rawField) : new FieldPTable(rawField);
      });
    }

    const pageSize = this.userPreferencesReport.transactionsPage.pagination?.pageSize;
    if (pageSize) {
      this.paginationDefault.pageSize = pageSize;
      if (this.paramsFilterTransactions?.pagination) {
        this.paramsFilterTransactions.pagination.pageSize = pageSize;
      }
    }
  }

  saveUserPreferences(): void {
    if (!this.userPreferencesReport?.transactionsPage) return;

    const isHidden = (f: any) => f.type === FieldPTableType.HIDDEN;
    const isAction = (f: any) => f.type === FieldPTableType.ACTION || f.key === "actions";

    const visibleGrid = (this.paramsFilterTransactions.fields as any[]).filter(
      (f) => f && f.show === true && !isHidden(f) && !isAction(f)
    );

    const gridFieldsPayload = visibleGrid.map((f, idx) => ({
      key: f.key,
      orderId: idx + 1,
      showGridFilter: !!f.showFilterField,
      showGridOrder: !!f.showOrderField,
      width: f.width,
    }));

    const visibleReport = (this.paramsFilterTransactions.fieldsReport as any[]).filter(
      (f) => f && f.show === true && !isHidden(f)
    );

    const reportFieldsPayload = visibleReport.map((f, idx) => ({
      key: f.key,
      orderId: idx + 1,
    }));

    const payload: any = {
      ...this.userPreferencesReport,
      version: "2",
      transactionsPage: {
        ...this.userPreferencesReport.transactionsPage,
        gridFields: gridFieldsPayload,
        reportFields: reportFieldsPayload,
        pagination: { pageSize: this.paramsFilterTransactions.pagination.pageSize },
      },
    };

    this.userPreferencesReport = payload;
    this.$services.userPreferences.saveUserPreferencesV2(payload);
  }

  downloadFileExport(): void {
    if (!this.selectTypeExport || this.selectTypeExport === "") return;
    const typeExport =
      typeof this.selectTypeExport === "string" ? this.selectTypeExport : String(this.selectTypeExport);
    const filters = this.filtersServerParams;
    const orderByFields: OrderByFields[] = [];
    this.paramsFilterTransactions.order.orderFields.map((orderField: any) => {
      orderByFields.push(Object.assign({}, { field: orderField.field, direction: orderField.direction }));
    });
    let delimiter = ";";
    if (typeExport === "CSV2") delimiter = ",";
    this.$services.transactions
      .fetchTransactionsFileExport(
        orderByFields,
        filters,
        this.fieldsToExport,
        this.translationsExport,
        typeExport === "CSV2" ? "CSV" : typeExport,
        delimiter,
        this.currentDecimalFormat
      )
      .then((file: File) => {
        const aElement = document.createElement("a");
        const url = URL.createObjectURL(file);
        aElement.href = url;
        aElement.download = file.name;
        aElement.click();
        window.URL.revokeObjectURL(url);
      });
  }

  get translationsExport(): { field: string; translation: { [key: string]: string } }[] {
    const translations: { field: string; translation: { [key: string]: string } }[] = [];
    const transactionTypes: { [key: string]: string } = {};
    Object.keys(ETransactionTypeList).map(
      (key: any) => (transactionTypes[key] = this.$tc("transactions.transactionType." + key))
    );
    translations.push({ field: "transactionType", translation: transactionTypes });

    const transactionOrigins: { [key: string]: string } = {};
    Object.keys(ETransactionOrigin).map(
      (key: any) => (transactionOrigins[key] = this.$tc("transactions.transactionOrigin." + key))
    );
    translations.push({ field: "origin", translation: transactionOrigins });

    const transactionEarlyValueAccreditationType: { [key: string]: string } = {};
    translations.push({ field: "amount.accreditation.type", translation: transactionEarlyValueAccreditationType });

    const transactionEarlyValueNotificationType: { [key: string]: string } = {};
    transactionEarlyValueNotificationType[EEarlyValueNotificationType.NOT] = this.$tc("common.yes");
    transactionEarlyValueNotificationType[EEarlyValueNotificationType.NO_NOT] = this.$tc("common.no");
    translations.push({ field: "amount.notification.type", translation: transactionEarlyValueNotificationType });

    return translations;
  }

  get fieldsToExport(): { field: string; label: string; orderId: string }[] {
    const excluded = [FieldPTableType.HIDDEN, FieldPTableType.ACTION];

    // Keep original index for fallback ordering
    const visible = this.paramsFilterTransactions.fieldsReport
      .map((f, idx) => ({ f, idx }))
      .filter(({ f }) => f.show && !excluded.includes(f.type));

    // Sort by persisted orderId, fallback to original position
    visible.sort((a, b) => {
      const ao = (a.f as any).orderId ?? a.idx + 1;
      const bo = (b.f as any).orderId ?? b.idx + 1;
      return ao - bo;
    });

    // Re-emit a clean, sequential order for the backend
    return visible.map(({ f }, pos) => ({
      field: f.key,
      label: f.label ?? "",
      orderId: String(pos + 1),
    }));
  }

  updateTransactionsFieldsPTable(): void {
    const gridPrefs = this.userPreferencesReport?.transactionsPage?.gridFields || [];
    const hasServerPrefs = gridPrefs.length > 0;
    const shouldApplyServerPrefs = hasServerPrefs && !this.userPrefsGridApplied;

    if (shouldApplyServerPrefs) {
      const merged = this.mergeFieldsWithPreferences(
        this.paramsFilterTransactions.defaultFields,
        this.extraDataFields,
        gridPrefs,
        false
      );

      this.paramsFilterTransactions.defaultFields = this.sortFieldsByOrderId(merged);
      this.paramsFilterTransactions.fields = [...this.paramsFilterTransactions.defaultFields];

      this.userPrefsGridApplied = true;
      return;
    }

    if (!this.paramsFilterTransactions.defaultFields || this.paramsFilterTransactions.defaultFields.length === 0) {
      this.paramsFilterTransactions.defaultFields = this.getColumnsDefault();
    }

    const defaultKeys = new Set(this.paramsFilterTransactions.defaultFields.map((f) => f.key));
    this.extraDataFields.forEach((f) => {
      if (!defaultKeys.has(f.key)) {
        this.paramsFilterTransactions.defaultFields.push(f);
        defaultKeys.add(f.key);
      }
    });

    if (!this.paramsFilterTransactions.fields || this.paramsFilterTransactions.fields.length === 0) {
      this.paramsFilterTransactions.fields = [...this.paramsFilterTransactions.defaultFields];
    } else {
      const fieldKeys = new Set(this.paramsFilterTransactions.fields.map((f) => f.key));
      this.extraDataFields.forEach((f) => {
        if (!fieldKeys.has(f.key)) {
          this.paramsFilterTransactions.fields.push(f);
        }
      });
    }
  }

  updateTransactionsFieldsReportPTable(): void {
    const reportPrefs = this.userPreferencesReport?.transactionsPage?.reportFields || [];
    const hasServerPrefs = reportPrefs.length > 0;
    const shouldApplyServerPrefs = hasServerPrefs && !this.userPrefsReportApplied;

    // ✅ First time: apply server preferences (backend) + defaults + extraData
    if (shouldApplyServerPrefs) {
      const merged = this.mergeFieldsWithPreferences(
        this.paramsFilterTransactions.defaultFieldsReport,
        this.extraDataFields,
        reportPrefs,
        true
      );

      const sortedMerged = this.sortFieldsByOrderId(merged);

      this.paramsFilterTransactions.defaultFieldsReport = sortedMerged;
      this.paramsFilterTransactions.fieldsReport = [...sortedMerged];

      this.userPrefsReportApplied = true;
      return;
    }

    if (
      !this.paramsFilterTransactions.defaultFieldsReport ||
      this.paramsFilterTransactions.defaultFieldsReport.length === 0
    ) {
      this.paramsFilterTransactions.defaultFieldsReport = this.getColumnsReportDefault();
    }

    const defaultKeys = new Set(this.paramsFilterTransactions.defaultFieldsReport.map((f) => f.key));
    this.extraDataFields.forEach((f) => {
      if (!defaultKeys.has(f.key)) {
        this.paramsFilterTransactions.defaultFieldsReport.push(f);
        defaultKeys.add(f.key);
      }
    });

    if (!this.paramsFilterTransactions.fieldsReport || this.paramsFilterTransactions.fieldsReport.length === 0) {
      this.paramsFilterTransactions.fieldsReport = [...this.paramsFilterTransactions.defaultFieldsReport];
    } else {
      const fieldKeys = new Set(this.paramsFilterTransactions.fieldsReport.map((f) => f.key));
      this.extraDataFields.forEach((f) => {
        if (!fieldKeys.has(f.key)) {
          this.paramsFilterTransactions.fieldsReport.push(f);
        }
      });
    }
  }

  areAnyExtraDataField(): boolean {
    return this.extraDataFields.length > 0;
  }
  getExtraDataFields(extraData: any): FieldPTable[] {
    const extraDataFields: FieldPTable[] = [];
    for (const key of Object.keys(extraData)) {
      const field = new FieldPTable({
        key: "extraData." + key,
        label: key,
        width: "110px",
        groupField: "" + i18n.t("transactions.field.groupFieldExtradaData"),
        showFilterField: false,
        show: false,
      });
      extraDataFields.push(field);
    }
    return extraDataFields;
  }
  /**
   * Merges default fields, extraData fields, and user preferences for grid or report.
   * Returns the unique merged fields array.
   */
  mergeFieldsWithPreferences(
    _defaultFields: FieldPTable[],
    extraDataFields: FieldPTable[],
    userPrefFields: any[],
    isReport = false
  ): FieldPTable[] {
    const canonicalDefaults = isReport ? this.getColumnsReportDefault() : this.getColumnsDefault();
    const canonical = this.getUniqueFieldsByKey([...canonicalDefaults, ...extraDataFields]);

    const prefList = Array.isArray(userPrefFields) ? userPrefFields : [];
    const prefByKey = new Map<string, any>();
    prefList.forEach((p: any, idx: number) => prefByKey.set(p.key, { ...p, __idx: idx }));

    const hasPrefs = prefByKey.size > 0;

    return canonical.map((base: any) => {
      const pref = prefByKey.get(base.key);

      const isFixed = !!base.fixedOrder || !!base.alwaysShow;

      if (!pref) {
        return {
          ...base,
          show: isFixed ? !!base.show : hasPrefs ? false : !!base.show,
          orderId: (base as any).orderId,
        };
      }

      const { label, groupField, __idx, ...rest } = pref;

      return {
        ...base,
        ...rest,
        show: pref.show ?? true,
        orderId: pref.orderId ?? __idx + 1,
      };
    });
  }

  updateFieldsFromRawFields(fields: FieldPTable[], rawFields: RawFieldPTable[]): FieldPTable[] {
    const returnTemp: FieldPTable[] = [];
    const fieldsNotSaved = fields.filter((fi) => !rawFields.find((rawField) => rawField.key == fi.key));
    rawFields.map((rawField) => {
      const fieldBase = fields.find((fi) => fi.key == rawField.key);
      const fieldCustomer = new FieldPTable(rawField);
      if (fieldBase) returnTemp.push(Object.assign({}, fieldBase, fieldCustomer));
    });
    if (fieldsNotSaved) returnTemp.push(...fieldsNotSaved);
    return returnTemp;
  }
  updateFieldsFromRawGridFields(fields: FieldPTable[], rawFields: RawGridField[]): FieldPTable[] {
    const returnTemp: FieldPTable[] = [];
    const fieldsNotSaved = fields.filter((fi) => !rawFields.find((rawField) => rawField.key == fi.key));
    rawFields.map((rawField) => {
      const fieldBase = fields.find((fi) => fi.key == rawField.key);
      const fieldCustomer = new FieldPTable(rawField);
      if (fieldBase) {
        fieldCustomer.showFilterField = rawField.showGridFilter;
        fieldCustomer.showOrderField = rawField.showGridOrder;
        fieldCustomer.show = true;
        returnTemp.push(fieldCustomer);
      }
    });
    if (fieldsNotSaved) {
      returnTemp.push(...fieldsNotSaved);
    }
    return returnTemp;
  }
  updateFieldsFromRawReportFields(fields: FieldPTable[], rawFields: RawReportField[]): FieldPTable[] {
    const returnTemp: FieldPTable[] = [];
    const fieldsNotSaved = fields.filter((fi) => !rawFields.find((rawField) => rawField.key == fi.key));
    rawFields.map((rawField) => {
      const fieldBase = fields.find((fi) => fi.key == rawField.key);
      const fieldCustomer = new FieldPTable(rawField);
      if (fieldBase) {
        fieldCustomer.show = true;
        returnTemp.push(fieldCustomer);
      }
    });
    if (fieldsNotSaved) {
      returnTemp.push(...fieldsNotSaved);
    }
    return returnTemp;
  }
  getUniqueFieldsByKey(fields: FieldPTable[]): FieldPTable[] {
    const seen = new Set();
    return fields.filter((f) => {
      if (seen.has(f.key)) return false;
      seen.add(f.key);
      return true;
    });
  }
  resetPagination(): void {
    const savedPageSize =
      this.paramsFilterTransactions?.pagination?.pageSize ??
      this.userPreferencesReport?.transactionsPage?.pagination?.pageSize ??
      this.paginationDefault.pageSize ??
      this.pageSize;

    this.paramsFilterTransactions.pagination = {
      ...this.paramsFilterTransactions.pagination,
      ...this.paginationDefault,
      pageSize: savedPageSize,
      pageSizeDefault: savedPageSize,
      currentPage: 1,
    };

    this.paramsFilterTransactions.order = { ...this.orderDefault };
  }

  async searchTransactions(filter: FilterTransations): Promise<void> {
    const dayInMilliseconds = 1000 * 3600 * 24;
    const transactionSearchLimit = this.getTransactionsSearchConfig.transactionSearchLimit;
    const machineTransactionsPerDayEstimated = this.getTransactionsSearchConfig.machineTransactionsPerDayEstimated;
    const daysSelected = (filter.dateEnd.getTime() - filter.dateStart.getTime()) / dayInMilliseconds;

    const filtersServerParams: any[] = [];

    const hasData = (obj: any) => obj && obj != "";

    const dateStart = LocalDateTime.ofInstant(Instant.parse(filter.dateStart.toISOString()));
    const dateEnd = LocalDateTime.ofInstant(Instant.parse(filter.dateEnd.toISOString()));
    const field = filter.searchBy ? "acreditationDate" : "transactionDate";
    filtersServerParams.push({ field: field, operator: FilterOperator.BETWEEN, value: dateStart + "," + dateEnd });

    const servicePoints: string[] = [];
    this.servicePoints
      ?.filter(hasData)
      .filter((servicePoint) => filter.smartDevices.includes(servicePoint.id))
      .forEach((servicePoint) => servicePoints.push(servicePoint.id));
    if (servicePoints && servicePoints.length > 0)
      filtersServerParams.push({
        field: "servicePointId",
        operator: FilterOperator.IN,
        value: servicePoints.toString(),
      });

    const logicCashTodays: string[] = [];
    this.logicCashTodays
      ?.filter(hasData)
      .filter((logicCashToday) => filter.smartDevices.includes(logicCashToday.id))
      .forEach((logicCashToday) => logicCashTodays.push(logicCashToday.id));
    if (logicCashTodays && logicCashTodays.length > 0)
      filtersServerParams.push({
        field: "logicCashTodayId",
        operator: FilterOperator.IN,
        value: logicCashTodays.toString(),
      });

    const transactionType: string[] = [];
    filter.transactionType.filter(hasData).forEach((tr) => transactionType.push(tr));
    if (transactionType.toString())
      filtersServerParams.push({
        field: "transactionType",
        operator: FilterOperator.IN,
        value: transactionType.toString(),
      });

    if (filter.entityName)
      filtersServerParams.push({
        field: "entity,name",
        operator: FilterOperator.FULL_TEXT_SEARCH,
        value: filter.entityName,
      });
    if (filter.userTransaction)
      filtersServerParams.push({
        field: "tellerName",
        operator: FilterOperator.FULL_TEXT_SEARCH,
        value: filter.userTransaction,
      });

    /** @TODO revisar estos parametros de isEarlyValue */
    if (filter.creditDateStart && filter.creditDateEnd) {
      const creditDateStart = LocalDateTime.ofInstant(Instant.parse(filter.creditDateStart.toISOString()));
      const creditDateEnd = LocalDateTime.ofInstant(Instant.parse(filter.creditDateEnd.toISOString()));
      filtersServerParams.push({
        field: "creditDate",
        operator: FilterOperator.BETWEEN,
        value: creditDateStart + "," + creditDateEnd,
      });
    }
    // siempre debe estar? o solo cuando es true?
    if (filter.isCheckAccreditation)
      filtersServerParams.push({
        field: "isCheckAccreditation",
        operator: FilterOperator.EQUALS,
        value: filter.isCheckAccreditation,
      });
    if (filter.entityNameAccrediting)
      filtersServerParams.push({
        field: "entityNameAccrediting",
        operator: FilterOperator.FULL_TEXT_SEARCH,
        value: filter.entityNameAccrediting,
      });

    const machinesSelected = servicePoints.length + logicCashTodays.length;
    this.filtersServerParams = filtersServerParams;
    if (daysSelected * machinesSelected * machineTransactionsPerDayEstimated > transactionSearchLimit) {
      await this.fetchCanExport();
      this.isExportDisabledBySearchLimit = true;
      this.$swal({
        title: this.$t("transactions.exportRequest.title").toString(),
        html: this.$t("transactions.exportRequest.message").toString(),
        icon: "question",
        showCancelButton: true,
        showDenyButton: true,
        showCloseButton: true,
        showConfirmButton: true,
        confirmButtonText: this.$t("transactions.exports.xls").toString(),
        denyButtonText: this.$t("transactions.exports.csv").toString(), // CSV (;)
        cancelButtonText: this.$t("button.cancel").toString(),
        confirmButtonColor: "#1D6F42",
        denyButtonColor: "#53af46",
        // inyectamos el botón extra CSV (,)
        didOpen: (el: HTMLElement) => {
          const actions = el.querySelector(".swal2-actions") as HTMLElement | null;
          const denyBtn = el.querySelector(".swal2-deny") as HTMLButtonElement | null;

          if (actions && denyBtn && !document.getElementById("csvCommaBtn")) {
            // clonamos el botón CSV existente
            const commaBtn = denyBtn.cloneNode(true) as HTMLButtonElement;
            commaBtn.id = "csvCommaBtn";
            // traducción
            const txt = (this.$t as any)?.("transactions.exports.csv2") || "Exportar en .CSV (,)";
            commaBtn.textContent = txt.toString();

            // lo insertamos antes de "Cancelar"
            const cancelBtn = el.querySelector(".swal2-cancel");
            if (cancelBtn) actions.insertBefore(commaBtn, cancelBtn);
            else actions.appendChild(commaBtn);

            // misma lógica, solo cambia el delimitador a coma
            commaBtn.addEventListener("click", () => {
              const requestBody: Record<string, unknown> = {};
              requestBody["filters"] = this.filtersServerParams;
              requestBody["sortFields"] = [{ field: "transactionDate", direction: "ASC" }];
              requestBody["reportFields"] = this.fieldsToExport;
              requestBody["translations"] = this.translationsExport;
              requestBody["typeExport"] = "CSV";
              requestBody["language"] = this.getBrowserLanguage();
              requestBody["typeDelimiter"] = ",";
              requestBody["decimalFormat"] = this.currentDecimalFormat;

              this.$services.exportRequest.saveTransactionsExportRequest(requestBody).then(() => {
                this.makeToast("success", this.$t("transactions.exportRequest.success").toString());
              });

              this.$swal.close();
            });
          }

          // Deshabilitamos si no puede solicitar más exportaciones
          const excelBtn = el.querySelector(".swal2-confirm") as HTMLButtonElement | null;
          const csvBtn = el.querySelector(".swal2-deny") as HTMLButtonElement | null;
          const csv2Btn = el.querySelector("#csvCommaBtn") as HTMLButtonElement | null;
          const message = this.canRequestExport ? "" : this.$t("transactions.exportRequest.disabled").toString();
          const disabled = !this.canRequestExport;
          if (excelBtn) {
            excelBtn.disabled = disabled;
            excelBtn.title = message;
          }
          if (csvBtn) {
            csvBtn.disabled = disabled;
            csvBtn.title = message;
          }
          if (csv2Btn) {
            csv2Btn.disabled = disabled;
            csv2Btn.title = message;
          }
        },
      }).then((result) => {
        if (result.isConfirmed || result.isDenied) {
          const requestBody: Record<string, unknown> = {};
          requestBody["filters"] = this.filtersServerParams;
          requestBody["sortFields"] = [{ field: "transactionDate", direction: "ASC" }];
          requestBody["reportFields"] = this.fieldsToExport;
          requestBody["translations"] = this.translationsExport;
          requestBody["typeExport"] = result.isConfirmed ? "XLSX" : "CSV";
          requestBody["language"] = this.getBrowserLanguage();
          if (!result.isConfirmed) {
            requestBody["typeDelimiter"] = ";";
          }
          requestBody["decimalFormat"] = this.currentDecimalFormat;

          this.$services.exportRequest.saveTransactionsExportRequest(requestBody).then(() => {
            this.makeToast("success", this.$t("transactions.exportRequest.success").toString());
          });
        }
      });
    } else {
      this.isExportDisabledBySearchLimit = false;
      this.resetPagination();
      this.iTranslationsTable = undefined;
      this.filters = JSON.parse(JSON.stringify(filter));
      this.filtersServerParams = filtersServerParams;

      this.giveMeTransactions();
    }
  }
  showSideBarPersonalize(): void {
    this.showPersonalize = true;
    this.widthSidebar = "";
    this.title = this.$t("transactions.personalize");
    this.showSideBar = true;
  }
  changeMainTable(paramsFilterTransactions: PTableType): void {
    this.paramsFilterTransactions.pagination = { ...paramsFilterTransactions.pagination };
    this.giveMeTransactions();
  }
  areFiltersSet(): boolean {
    return this.filters.smartDevices && this.filters.smartDevices.length > 0 && this.filters.transactionType.length > 0;
  }
  areTransactions(): boolean {
    return this.transactionItems.length > 0;
  }
  giveMeTransactions(): void {
    if (!this.areFiltersSet()) return;

    const limit = this.paramsFilterTransactions.pagination.pageSize;
    const offset =
      (this.paramsFilterTransactions.pagination.currentPage - 1) * this.paramsFilterTransactions.pagination.pageSize;

    const orderByFields: OrderByFields[] = [];
    this.paramsFilterTransactions.order.orderFields.map((orderField: any) => {
      orderByFields.push(Object.assign({}, { field: orderField.field, direction: orderField.direction }));
    });

    const filters = this.filtersServerParams;

    this.$services.transactions.fetchTransactions(limit, offset, orderByFields, filters).then((resp) => {
      this.transactions = [...resp.transactions];
      this.transactionItems = [...resp.transactions];

      let transactionItemsNew: TransactionItem[] = [];
      let allExtraDataCombined: any = {};

      this.transactionItems.map((transactionItem) => {
        transactionItem.tableKey = transactionItem.transactionSingleOriginId;

        this.flatExtraDatas(transactionItem);
        allExtraDataCombined = Object.assign(allExtraDataCombined, transactionItem.extraData);

        const transactionItemsTemp: TransactionItem[] = [];
        const currencies = this.getTransactionCurrencies(transactionItem);
        currencies.map((currency) => {
          const transactionItemNew = this.createTransactionItemByCurrency(transactionItem, currency);
          if (transactionItemNew.amounts.length > 0) transactionItemsTemp.push(transactionItemNew);
        });

        transactionItemsNew = transactionItemsNew.concat(
          this.removeDuplicatePropertiesFromTransactionMultipleCurrencies(transactionItemsTemp)
        );
      });

      this.transactionItems = transactionItemsNew;
      this.paramsFilterTransactions.pagination.totalElements = resp.totalResults;

      this.extraDataFields = this.getExtraDataFields(allExtraDataCombined);

      this.userPrefsGridApplied = false;
      this.userPrefsReportApplied = false;

      this.updateTransactionsFieldsPTable();
      this.updateTransactionsFieldsReportPTable();
    });
  }

  getMergedExtraDataField(): any {
    const extraData = {};
    this.transactionItems.map((field) => Object.assign(extraData, field.extraData));
    return extraData;
  }
  flatExtraDatas(transactionItem: TransactionItem) {
    const extraData: any = transactionItem.extraDatas?.reduce(
      (acc: any, curr: any) => ((acc[curr["name"]] = curr["value"]), acc),
      {}
    );
    transactionItem.extraData = extraData;
  }
  removeDuplicatePropertiesFromTransactionMultipleCurrencies(transactionItems: TransactionItem[]): TransactionItem[] {
    const transactionItemsNew: TransactionItem[] = [];
    transactionItemsNew.push(transactionItems[0]);
    transactionItemsNew[0].barCodes = transactionItems[0].amount.barCodes.join(", ");
    for (const item of transactionItems.slice(1)) {
      const itemNew = Object.assign(
        {},
        {
          tableKey: item.transactionSingleOriginId,
          id: item.id,
          amount: item.amount,
          amounts: item.amounts,
          currency: item.currency,
          currencies: item.currencies,
          denominations: item.denominations,
          barCodes: item?.amount?.barCodes?.join(", ") ?? "",
        }
      );
      transactionItemsNew.push(itemNew);
    }
    return transactionItemsNew;
  }

  createTransactionItemByCurrency(transactionItem: TransactionItem, iso: string) {
    let amounts: any[] = [];
    let amount = {};
    if (transactionItem.amounts) {
      amounts = transactionItem.amounts.filter((prop) => prop.currency == iso);
      amount = amounts.length > 0 ? amounts[0] : {};
    }
    let currencies: any[] = [];
    let currency = {};
    if (transactionItem.currencies) {
      currencies = transactionItem.currencies.filter((prop) => prop.currency == iso);
      currency = currencies.length > 0 ? currencies[0] : {};
    }
    let denominations: any[] = [];
    if (transactionItem.denominations) {
      denominations = transactionItem.denominations.filter((prop) => prop.currency == iso);
    }
    return Object.assign({}, transactionItem, { amount, amounts, currencies, currency, denominations });
  }
  getTransactionCurrencies(transactionItem: TransactionItem): string[] {
    const currencies = new Set<string>();
    transactionItem.currencies?.forEach((c) => currencies.add(c.currency));
    transactionItem.amounts?.forEach((a) => currencies.add(a.currency));
    transactionItem.denominations?.forEach((d) => currencies.add(d.currency));
    return Array.from(currencies);
  }
  viewTransaction(rowTransaction: TransactionItem): void {
    const transaction = this.transactions.find((tr) => tr.transactionSingleOriginId == rowTransaction.tableKey);
    if (!transaction) return;

    this.title = this.$t("transactions.detailsTitle");
    this.selectedTransaction = Object.assign({}, transaction);
    this.showPersonalize = false;
    this.widthSidebar = "1213px";
    this.showSideBar = true;
    this.showDetail = true;
  }
  tableVersion = 0;
  changePersonalize(newParams: PTableType): void {
    // replace everything with what PPersonalizeTable emitted
    this.paramsFilterTransactions = { ...newParams };
    this.tableVersion++;
    // close sidebar and persist prefs (optional)
    this.showPersonalize = false;
    this.showSideBar = false;
    this.showDetail = false;
    this.saveUserPreferences(); // you already rebuild orderIds here if you want
  }

  /**
   * Resets the table columns to only the default columns, clearing user preferences and extraData.
   * Called when the user clicks "Restaurar" in the personalization sidebar.
   */
  resetColumnsToDefault(): void {
    // Clear user preferences for grid and report fields
    if (this.userPreferencesReport && this.userPreferencesReport.transactionsPage) {
      this.userPreferencesReport.transactionsPage.gridFields = [];
      this.userPreferencesReport.transactionsPage.reportFields = [];
      this.userPreferencesReport.transactionsPage.pagination = { pageSize: this.pageSize };
    }
    // Clear customer fields
    this.transactionsCustomerFields = [];
    this.transactionsCustomerReportFields = [];

    // Restore only the default columns, but include extraData if present in current transactions
    let extraDataFields: FieldPTable[] = [];
    if (this.transactionItems && this.transactionItems.length > 0) {
      const allExtraData = this.getMergedExtraDataField();
      extraDataFields = this.getExtraDataFields(allExtraData);
    }
    this.extraDataFields = extraDataFields;

    // Merge defaults + extraData for grid and report
    const defaultFields = this.getColumnsDefault();
    const defaultFieldsReport = this.getColumnsReportDefault();
    this.paramsFilterTransactions.defaultFields = this.getUniqueFieldsByKey([...defaultFields, ...extraDataFields]);
    this.paramsFilterTransactions.fields = [...this.paramsFilterTransactions.defaultFields];
    this.paramsFilterTransactions.defaultFieldsReport = this.getUniqueFieldsByKey([
      ...defaultFieldsReport,
      ...extraDataFields,
    ]);
    this.paramsFilterTransactions.fieldsReport = [...this.paramsFilterTransactions.defaultFieldsReport];

    // Save cleared preferences
    this.saveUserPreferences();
    // Optionally, reset pagination/order if desired
    this.resetPagination();
  }
}
